import { useEffect, useMemo } from 'react';

import { DEFAULT_DOCUMENT_TITLE, documentTitleFromMarkdown } from '../api/documentMarkdown';
import type { DocumentDraft } from '../model/documentDraftTypes';
import { useDocumentPdfPreviewQuery } from '../model/useDocumentPdfPreviewQuery';

import { DocumentCard } from '@/shared/ui/document-editor';

function formatDraftDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );
}

interface DocumentDraftCardProps {
  patientId: string;
  draft: DocumentDraft;
  defaultTitle?: string;
  onSelect(): void;
}

export function DocumentDraftCard({
  patientId,
  draft,
  defaultTitle,
  onSelect,
}: DocumentDraftCardProps) {
  const previewQuery = useDocumentPdfPreviewQuery(patientId, draft);
  const previewUrl = useMemo(
    () => (previewQuery.data === undefined ? null : URL.createObjectURL(previewQuery.data)),
    [previewQuery.data]
  );

  useEffect(() => {
    return () => {
      if (previewUrl !== null) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const title = documentTitleFromMarkdown(
    draft.content.bodyMarkdown,
    defaultTitle ?? DEFAULT_DOCUMENT_TITLE
  );
  const editedBy = draft.modifiedBy.displayName ? ` · ${draft.modifiedBy.displayName}` : '';
  return (
    <DocumentCard
      title={title}
      details={[`Draft · ${formatDraftDate(draft.updatedAt)}${editedBy}`]}
      previewUrl={previewQuery.isError ? null : previewUrl}
      previewLoading={previewQuery.isLoading}
      ariaLabel={`Edit ${title}`}
      onSelect={onSelect}
    />
  );
}
